// Every string FilterRow can render, and the shape a caller may replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, next door in bidCardTranslations.js.
//
// What is worth saying here is why a row this small needs the mechanism at all.
// Three of the words on a filter row are the ROW's, not the caller's: the
// summary shown when nothing is chosen, the count that replaces it, and the
// accessible name on each chip's remove control. The label beside them arrives
// already translated, so before this prop existed a French dealer read
// "Près de moi" and "Any" on one line — with the translation for the second one
// already sitting in the same catalog the first came from.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds
// that a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

export { interpolate } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the row. A translation may move them
 * around its sentence or drop one it does not need; it may not invent one,
 * since the row only substitutes the names it knows.
 */
export const FILTER_ROW_TRANSLATIONS = Object.freeze({
  // The summary line when the caller passes no value. It is also what
  // `isValueSet` compares against, so a summary that IS this word reads as
  // unset in whatever language the row is speaking — which is the whole reason
  // the comparison cannot stay a literal.
  any: 'Any',
  // Replaces the summary the moment there are chips. The row substitutes and
  // does not conjugate: a language wanting singular and plural agreement gets
  // it by the caller passing the template it wants for the count it holds,
  // which is the same trade BidCard's catalog makes.
  selected: '{count} selected',
  // The accessible name on a chip's remove control. `{label}` is the chip's
  // displayed text, which for an object chip is its `label` and not its value —
  // an auction house's name rather than its identifier.
  removeChip: 'Remove {label}',
});

/**
 * The keys in `translations` that FilterRow would not read, as dotted paths.
 *
 * The walk itself is BidCard's — it already takes the defaults object as an
 * argument, so this surface passes its own rather than owning a second copy of
 * a comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, FILTER_ROW_TRANSLATIONS);

/**
 * One language's text over the English defaults, key by key.
 *
 * A flat spread, because the namespace is flat — there is no nested group here
 * for a partial object to blank out the way BidCard's `lightNames` would.
 */
export function mergeTranslations(translations) {
  if (!translations) return FILTER_ROW_TRANSLATIONS;

  return { ...FILTER_ROW_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English. There is no
 * `formatMoney` member as there is on the card and the tray: this row renders
 * no money and should not take a formatter for one.
 */
export function isValidFilterRowI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
